// ── usePositionPnl — Shared P&L calculation hook ────────────────
// Used by both PositionTracker and ValueDashboard's PositionSidebar.
// Derives unrealized P&L from accountStore positions + marketStore prices.

import { useAccountStore } from '../../stores/accountStore';
import { useMarketStore } from '../../stores/marketStore';
import { estimateAggregateFeeCents, takerRateForTicker } from '../../lib/pricing/fees';

export interface PositionWithPnl {
  ticker: string;
  side: 'yes' | 'no';
  position: number;
  averagePrice: number;
  currentPrice: number | null;
  feesPaidCents?: number;
  /** Entry cost + fees actually paid so far. */
  totalCostCents: number;
  /** Mark-to-market (current × qty). Null when current price unknown.
   *  Gross notional — does NOT account for sell-side fees. Use
   *  `cashOutCents` when displaying what the user would actually receive
   *  on a market sell. */
  totalValueCents: number | null;
  /** Estimated taker fee to close the whole position at the current mark.
   * Null when current price or fee rate unknown. */
  estClosingFeeCents: number | null;
  /** Live liquidation value = totalValueCents − estClosingFeeCents — what
   *  actually hits the account on a market sell. Null when current price
   *  is unknown. When the close-fee rate isn't loaded yet we fall back to
   *  totalValueCents (no fee subtraction) so the column still renders;
   *  callers can flag the missing fee via tooltip on estClosingFeeCents. */
  cashOutCents: number | null;
  /** Net realized if the position settles WIN (100¢ payout, no settle fees). */
  ifWinCents: number;
  /** Live exit P&L: value − cost − estClosingFee. Closing fee is treated as 0
   * when unknown so the column still renders; row tooltips can flag that. */
  unrealizedPnl: number | null;
}

export function usePositionPnl() {
  const positions = useAccountStore((s) => s.positions);
  const version = useMarketStore((s) => s.version);
  const getMarketPrice = useMarketStore((s) => s.getMarketPrice);

  // Force re-derive when Hydra prices update
  void version;

  const positionsWithPnl: PositionWithPnl[] = positions.map((pos) => {
    // marketStore returns the YES mid; flip to side-native for NO so the
    // value compares directly against averagePrice (which is the actual
    // price paid for the side held — 72c on a NO contract means the user
    // paid 72c for that NO contract, not the YES-equivalent).
    const yesMid = getMarketPrice(pos.ticker);
    const currentPrice =
      yesMid == null ? null : pos.side === 'yes' ? yesMid : 100 - yesMid;

    const feesPaid = pos.feesPaidCents ?? 0;
    // Prefer the relay's exact basis when present; avg × qty re-multiplies a
    // whole-cent-rounded avg and drifts on fractional/many-fill positions
    // (I-035). Personal-key positions (no relay ledger) keep the fallback.
    const entryCostCents = pos.costBasisCents ?? pos.averagePrice * pos.position;
    const totalCostCents = entryCostCents + feesPaid;
    const totalValueCents = currentPrice !== null ? currentPrice * pos.position : null;
    const ifWinCents = 100 * pos.position - totalCostCents;

    // Closing-fee estimate uses the taker rate (closer typically crosses
    // the spread). Symmetric in P × (1−P), so passing side-native cents
    // gives the same answer for YES vs NO. Uses the canonical aggregate
    // ceil — the fee Kalshi charges to close the whole position in one
    // execution, rounded up to the cent. No calibration coefficient: the
    // multi-fill premium it modeled does not exist.
    const takerRate = takerRateForTicker(pos.ticker);
    const estClosingFeeCents =
      currentPrice !== null && takerRate !== null
        ? estimateAggregateFeeCents({
            priceCents: currentPrice,
            count: pos.position,
            feeRate: takerRate,
          })
        : null;

    const cashOutCents =
      totalValueCents !== null
        ? totalValueCents - (estClosingFeeCents ?? 0)
        : null;
    const unrealizedPnl =
      cashOutCents !== null ? cashOutCents - totalCostCents : null;

    return {
      ...pos,
      currentPrice,
      feesPaidCents: pos.feesPaidCents,
      totalCostCents,
      totalValueCents,
      estClosingFeeCents,
      cashOutCents,
      ifWinCents,
      unrealizedPnl,
    };
  });

  const totalPnl = positionsWithPnl.reduce((sum, p) => sum + (p.unrealizedPnl ?? 0), 0);

  return { positionsWithPnl, totalPnl };
}
